import { useMutation, useQueryClient } from "react-query";
import { request } from "../../../config/helpers/axios-instance";
import { throwIfRequestError } from "../../hooks/request-error";

// PATCH /api/inventory/orders/:id/receive — receiveOrder(). `quantityReceived` per line
// is a DELTA added to the line's running total, not a new absolute total.
//
// `orderId` is a per-call argument (not a hook-construction argument) so ONE hook
// instance can receive against several purchase orders in sequence — the shortage
// screen's Receive modal (§Phase-7-UI) does exactly that when a medicine's open lines
// span more than one order, issuing one PATCH per order via repeated mutateAsync calls.
// The PO detail page (single order per page) just always passes the same id.
export default function useReceivePurchaseOrderLines() {
    const queryClient = useQueryClient();

    return useMutation(
        async ({ orderId, lines }) => {
            const result = await request({
                url: `inventory/orders/${orderId}/receive`,
                method: "PATCH",
                data: { lines },
            });
            throwIfRequestError(result);
            const body = result.data;
            if (!body?.success) throw body;
            return body.data;
        },
        {
            onSuccess: (_data, variables) => {
                queryClient.invalidateQueries(["pharmacy-purchase-order-detail", variables.orderId]);
                queryClient.invalidateQueries(["pharmacy-purchase-orders"]);
                // A receive can change a shortage row's status (Requested/Delayed ->
                // Pending) and inTransit/purchase figures — this screen reads the same
                // inventoryOrderItems the PO pages do (getPurchaseOrderStatus), so it
                // must be invalidated here too, not just from the shortages screen's own
                // create-order flow (useCreatePurchaseOrders).
                queryClient.invalidateQueries(["pharmacy-procurement-shortages"]);
            },
        }
    );
}
